import { useState, useEffect, useCallback, useMemo } from 'react';

import { client } from '@admin/libraries/apollo';
import {
  useMessagesQuery,
  useOutgoingMessagesAllowedQuery,
  useMessagesWidgetBuildMessageMutation,
  useMessageThreadResolveMutation,
  MessageInput,
  Pagination as PaginationType,
  MessagesQueryVariables,
} from '@admin/schema';

import {
  MessageFromQuery,
  OutgoingMessagesAllowedVariables,
  LoadingState,
  MutationError,
  PhoneWithName,
  Recipient,
  MessagesWidgetOrder,
} from '../types';

export const useMessagesData = (
  recipient: Recipient,
  recipientType: 'Account' | 'Lead',
  order?: MessagesWidgetOrder,
) => {
  // GraphQL Hooks
  const [buildMessageMutation] = useMessagesWidgetBuildMessageMutation({ client });
  const [resolveMessageThreadMutation] = useMessageThreadResolveMutation({ client });

  // State
  const [messages, setMessages] = useState<MessageFromQuery[]>([]);
  const [messageBody, setMessageBody] = useState('');
  const [phoneList, setPhoneList] = useState<PhoneWithName[]>([]);
  const [phoneToSendTo, setPhoneToSendTo] = useState<PhoneWithName | undefined>(undefined);
  const [phoneToFilterBy, setPhoneToFilterBy] = useState<PhoneWithName | undefined>(undefined);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [pagination, setPagination] = useState<PaginationType>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });
  const [messagesVars, setMessagesVars] = useState<MessagesQueryVariables | undefined>(undefined);
  const [outgoingVars, setOutgoingVars] = useState<OutgoingMessagesAllowedVariables | undefined>(undefined);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    messages: false,
    sendingMessage: false,
    resolvingThread: false,
  });
  const [error, setError] = useState<MutationError | undefined>(undefined);

  // GraphQL Queries
  const { data: messagesData, refetch: refetchMessages } = useMessagesQuery({
    client,
    variables: messagesVars,
    skip: !messagesVars,
    fetchPolicy: 'network-only',
  });

  const { refetch: refetchOutgoingMessagesAllowed } = useOutgoingMessagesAllowedQuery({
    client,
    variables: outgoingVars,
    skip: !outgoingVars,
    fetchPolicy: 'network-only',
  });

  // Helper functions
  const getOrderId = useCallback(() => {
    if (!order) return null;
    return order.parentID || order.id;
  }, [order]);

  const recipientHasPhone = useCallback(() => {
    if (recipientType === 'Account') {
      return !!phoneToSendTo;
    } else if (recipientType === 'Lead') {
      return !!phoneToSendTo;
    }
    return false;
  }, [recipientType, phoneToSendTo]);

  const loadMessages = useCallback(
    async (page = 1) => {
      try {
        setLoadingState((prev) => ({ ...prev, messages: true }));
        setError(undefined);

        const orderId = getOrderId();
        const params: MessagesQueryVariables = {
          recipientType,
          recipientIds: [recipient.id],
          page,
          perPage: 25,
        };

        if (orderId) {
          params.orderId = orderId;
        }

        setMessagesVars(params);

        // Always trigger refetch for refresh functionality
        await refetchMessages(params);
      } catch (err) {
        setError({ message: 'Failed to load messages' });
      } finally {
        setLoadingState((prev) => ({ ...prev, messages: false }));
      }
    },
    [recipientType, recipient.id, refetchMessages, getOrderId],
  );

  const sendMessage = useCallback(async () => {
    if (!messageBody.trim() || !phoneToSendTo) return;

    const prevSmsEnabled = smsEnabled;
    try {
      setLoadingState((prev) => ({ ...prev, sendingMessage: true }));
      setError(undefined);
      setSmsEnabled(false);

      const messageInput: MessageInput = {
        body: messageBody,
        recipientType,
        recipientId: recipient.id,
        phoneId: phoneToSendTo.id,
      };

      const orderId = getOrderId();
      if (orderId) {
        messageInput.orderId = orderId;
      }

      await buildMessageMutation({
        variables: { input: messageInput },
      });

      setMessageBody('');
      await refetchMessages();
    } catch (err) {
      setError({ message: 'Failed to send message' });
    } finally {
      setLoadingState((prev) => ({ ...prev, sendingMessage: false }));
      setSmsEnabled(prevSmsEnabled);
    }
  }, [
    messageBody,
    phoneToSendTo,
    recipientType,
    recipient,
    buildMessageMutation,
    refetchMessages,
    getOrderId,
    smsEnabled,
  ]);

  const resolveMessageThread = useCallback(
    async (message: MessageFromQuery) => {
      if (!window.confirm('Are you sure you want to mark this message as resolved?')) {
        return;
      }

      const prevSmsEnabled = smsEnabled;
      try {
        setLoadingState((prev) => ({ ...prev, resolvingThread: true }));
        setError(undefined);
        setSmsEnabled(false);

        const params: { recipientType: string; recipientIds: string[] } = {
          recipientType,
          recipientIds: [recipient.id],
        };

        await resolveMessageThreadMutation({
          variables: params,
        });

        // Update local state - remove resolvable from the resolved message
        setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, resolvable: false } : m)));
      } catch (err) {
        setError({ message: 'Failed to resolve message thread' });
      } finally {
        setLoadingState((prev) => ({ ...prev, resolvingThread: false }));
        setSmsEnabled(prevSmsEnabled);
      }
    },
    [recipientType, resolveMessageThreadMutation, recipient, smsEnabled],
  );

  const loadPreferences = useCallback(async () => {
    const number = phoneToSendTo?.number;

    if (!number) {
      setSmsEnabled(false);
      return;
    }

    try {
      setError(undefined);

      // Check outgoing allowed
      const params: OutgoingMessagesAllowedVariables = {
        recipientType,
        number,
        recipientIds: [recipient.id],
      };

      setOutgoingVars(params);
      const result = await refetchOutgoingMessagesAllowed(params);
      const allowed = result.data?.outgoingMessagesAllowed !== false; // Default to true

      const hasPhone = recipientHasPhone();
      setSmsEnabled(hasPhone && allowed);
    } catch (err) {
      // In case of error, default to allowed
      setSmsEnabled(recipientHasPhone());
    }
  }, [phoneToSendTo, recipientType, recipient.id, refetchOutgoingMessagesAllowed, recipientHasPhone]);

  // Filter messages by selected phone
  const filteredMessages = useMemo(() => {
    if (!phoneToFilterBy) return messages;

    const filtered = messages.filter((m) => String(m.phone.id) === String(phoneToFilterBy.id));

    return filtered;
  }, [messages, phoneToFilterBy]);

  // Update local state when messages data loads
  useEffect(() => {
    if (messagesData?.paginatedMessages) {
      setMessages(messagesData.paginatedMessages.results);
      setPagination(messagesData.paginatedMessages.pagination);
    }
  }, [messagesData]);

  return {
    // State
    messages,
    messageBody,
    setMessageBody,
    phoneList,
    setPhoneList,
    phoneToSendTo,
    setPhoneToSendTo,
    phoneToFilterBy,
    setPhoneToFilterBy,
    smsEnabled,
    setSmsEnabled,
    pagination,
    loadingState,
    error,
    setError,
    filteredMessages,

    // Actions
    loadMessages,
    sendMessage,
    resolveMessageThread,
    loadPreferences,
    recipientHasPhone,

    // GraphQL data
    messagesData,
  };
};
